import { useState, useEffect } from 'react';
import { useStableAccessToken } from '@app/GranularDeploy/hooks/useStableAccessToken';
import {
  apiOverlayCandidates,
  apiSessionInUse,
  apiResolvePR,
  apiStopPreview,
  apiPreviewStopAvailable,
  OverlayCandidate,
  BaselineCandidate,
  InfraService,
  PRResolution,
  ServiceDependency,
} from '../helpers/apiHelpers';
export { usePreviewJobStatus as usePreviewDeploy } from '../context/PreviewJobStatusContext';

// ---------------------------------------------------------------------------
// useOverlayCandidates
// Fetches overlay candidates for the given branches + namespace.
// Re-fetches whenever branches or namespace changes (debounced by the caller).
// ---------------------------------------------------------------------------
export function useOverlayCandidates(branches: string[], namespace: string, dbBranch?: string, prUrls?: string[], onlyPr?: boolean) {
  const tokenGetter = useStableAccessToken();
  const [candidates, setCandidates] = useState<OverlayCandidate[]>([]);
  const [baselineCandidates, setBaselineCandidates] = useState<BaselineCandidate[]>([]);
  const [infraServices, setInfraServices] = useState<InfraService[]>([]);
  const [infraDepsMap, setInfraDepsMap] = useState<Record<string, string[]>>({});
  const [serviceDepsMap, setServiceDepsMap] = useState<Record<string, ServiceDependency[]>>({});
  const [suggestedServices, setSuggestedServices] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branchesKey = branches.join(',');
  const prUrlsKey = (prUrls ?? []).join(',');

  useEffect(() => {
    if (branches.length === 0 || !namespace) {
      setCandidates([]);
      setBaselineCandidates([]);
      setInfraServices([]);
      setInfraDepsMap({});
      setServiceDepsMap({});
      setSuggestedServices([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiOverlayCandidates(branches, namespace, tokenGetter, dbBranch, undefined, prUrls, onlyPr)
      .then(({ candidates: c, baselineCandidates: bc, infraServices: i, infraDepsMap: d, serviceDepsMap: sd, suggestedServices: ss, errors: partialErrors }) => {
        if (!cancelled) {
          setCandidates(c);
          setBaselineCandidates(bc);
          setInfraServices(i);
          setInfraDepsMap(d);
          setServiceDepsMap(sd);
          setSuggestedServices(ss);
          if (partialErrors?.length) {
            setError(`Some services could not be validated (${partialErrors.length} error${partialErrors.length > 1 ? 's' : ''}): ${partialErrors.join('; ')}`);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to fetch overlay candidates');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchesKey, namespace, dbBranch, prUrlsKey, onlyPr]);

  return { candidates, baselineCandidates, infraServices, infraDepsMap, serviceDepsMap, suggestedServices, loading, error };
}

// ---------------------------------------------------------------------------
// useSessionInUse
// Checks uniqueness of a previewTag within a namespace.
// Returns undefined while loading.
// ---------------------------------------------------------------------------
export function useSessionInUse(namespace: string, previewTag: string, refreshKey = 0) {
  const tokenGetter = useStableAccessToken();
  const [inUse, setInUse] = useState<boolean | undefined>(undefined);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!namespace || !previewTag) {
      setInUse(undefined);
      return;
    }
    let cancelled = false;
    setChecking(true);

    apiSessionInUse(namespace, previewTag, tokenGetter)
      .then((result) => { if (!cancelled) setInUse(result); })
      .catch(() => { if (!cancelled) setInUse(false); })  // treat errors as "not in use" so Next isn't blocked
      .finally(() => { if (!cancelled) setChecking(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, previewTag, refreshKey]);

  return { inUse, checking };
}

// ---------------------------------------------------------------------------
// usePreviewStop
// Terminates a running preview session via POST /api/preview/stop.
// ---------------------------------------------------------------------------
export function usePreviewStop() {
  const tokenGetter = useStableAccessToken();
  const [isStopping, setIsStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);

  const stopPreview = async (namespace: string, previewTag: string): Promise<boolean> => {
    setIsStopping(true);
    setStopError(null);
    try {
      await apiStopPreview(namespace, previewTag, tokenGetter);
      return true;
    } catch (err: any) {
      setStopError(err.message || 'Failed to stop preview session');
      return false;
    } finally {
      setIsStopping(false);
    }
  };

  return { stopPreview, isStopping, stopError };
}

// ---------------------------------------------------------------------------
// usePreviewStopAvailable
// Whether POST /api/preview/stop is actually reachable for the current cluster (the router is
// only mounted under ENABLE_PREVIEW_ENDPOINT, and the route only accepts qa/dev/int environments).
// Defaults to false until the check resolves, so the Stop button doesn't flash in then out.
// ---------------------------------------------------------------------------
export function usePreviewStopAvailable() {
  const tokenGetter = useStableAccessToken();
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiPreviewStopAvailable(tokenGetter).then((result) => {
      if (!cancelled) setAvailable(result);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return available;
}

// ---------------------------------------------------------------------------
// usePRBranch
// Resolves a full GitHub PR URL → head branch via the backend.
// Re-runs when prUrl changes.
// ---------------------------------------------------------------------------
// Retries once on a transient failure (429 rate-limit, or a 5xx blip) before giving up — the kind
// of "works on the second try" flakiness devs were hitting with no explanation on the first try.
const PR_RETRY_DELAY_MS = 2000;

async function resolvePrWithRetry(prUrl: string, tokenGetter: () => Promise<string>): Promise<PRResolution> {
  try {
    return await apiResolvePR(prUrl, tokenGetter);
  } catch (err: any) {
    const status = err?.status;
    if (status !== 429 && !(status >= 500 && status < 600)) throw err;
    await new Promise((resolve) => setTimeout(resolve, PR_RETRY_DELAY_MS));
    return apiResolvePR(prUrl, tokenGetter);
  }
}

export function usePRBranch(prUrl: string | null) {
  const tokenGetter = useStableAccessToken();
  const [resolution, setResolution] = useState<PRResolution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!prUrl) {
      setResolution(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    resolvePrWithRetry(prUrl, tokenGetter)
      .then((r) => { if (!cancelled) { setResolution(r); setError(null); } })
      .catch((err) => {
        if (!cancelled) {
          setResolution(null);
          setError(err.message || 'PR not found');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prUrl]);

  return { resolution, loading, error };
}
